/**
 * Pak Cloth House - Luxury Quick View Controller
 * Inspired by premium Pakistani fashion storefronts (Maria.B reference layout).
 * 
 * Features:
 * - Dynamic AJAX fetching via Shopify's /products/${handle}.js
 * - Two-column Maria.B layout: Left image gallery with vertical/horizontal thumbnails
 * - Dynamic variant option pills with real-time availability and pricing updates
 * - In Stock / Sold Out indicator badge
 * - Strikethrough compare-at price with calculate discount %
 * - Estimated shipping badge
 * - Quantity increment/decrement
 * - Direct seamless integration into Dawn's cart-drawer
 * - Collapsible Description accordion
 * - Accessible keyboard & backdrop dismiss
 */

(function () {
  'use strict';

  // Config fallback & translation sanitizer
  const rawConfig = window.pchQuickViewConfig || {};
  function sanitizeLabel(val, fallback) {
    if (!val || typeof val !== 'string' || val.toLowerCase().includes('translation missing')) {
      return fallback;
    }
    return val;
  }

  const config = {
    moneyFormat: rawConfig.moneyFormat || 'Rs. {{amount}}',
    shippingTime: sanitizeLabel(rawConfig.shippingTime, '3-5 BUSINESS DAYS'),
    inStockText: sanitizeLabel(rawConfig.inStockText, 'In Stock'),
    soldOutText: sanitizeLabel(rawConfig.soldOutText, 'Sold Out'),
    addToCartText: sanitizeLabel(rawConfig.addToCartText, 'ADD TO CART'),
    addingToCartText: sanitizeLabel(rawConfig.addingToCartText, 'ADDING...'),
    whatsappPhone: rawConfig.whatsappPhone || '',
    shopUrl: rawConfig.shopUrl || ''
  };

  let modalEl = null;
  let loadingEl = null;
  let contentEl = null;

  let currentProduct = null;
  let currentVariant = null;
  let selectedOptions = [];
  let currentImageIndex = 0;
  let quantity = 1;

  // --- Utility: Money Formatter ---
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || config.moneyFormat || 'Rs. {{amount}}';

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    const match = formatString.match(placeholderRegex);
    const type = match ? match[1] : 'amount';

    switch (type) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  // --- Utility: HTML Escape ---
  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // --- Utility: Shopify Image Sizing ---
  function getSizedImageUrl(src, size) {
    if (!src) return '';
    if (src.startsWith('//')) src = 'https:' + src;
    try {
      const url = new URL(src);
      const widthMatch = size.match(/^(\d+)x?$/);
      if (widthMatch) {
        url.searchParams.set('width', widthMatch[1]);
        return url.toString();
      }
    } catch (e) {}
    return src.replace(/\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i, `_${size}.$1$2`);
  }

  // --- Init Elements ---
  function initElements() {
    modalEl = document.getElementById('PchQuickView');
    if (!modalEl) return false;
    loadingEl = document.getElementById('PchQvLoading');
    contentEl = document.getElementById('PchQvContent');
    return true;
  }

  // --- Open Quick View ---
  function openQuickView(handle, fallbackUrl) {
    if (!initElements()) return;

    // Reset previous product state completely
    currentProduct = null;
    currentVariant = null;
    selectedOptions = [];
    currentImageIndex = 0;
    quantity = 1;

    modalEl.classList.add('is-active');
    modalEl.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    loadingEl.style.display = 'flex';
    contentEl.style.display = 'none';
    contentEl.innerHTML = '';

    fetch(`/products/${handle}.js`)
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load product: ${res.status}`);
        return res.json();
      })
      .then(product => {
        renderQuickView(product);
        loadingEl.style.display = 'none';
        contentEl.style.display = 'flex';
      })
      .catch(err => {
        console.error('PCH Quick View error:', err);
        loadingEl.innerHTML = `
          <div style="padding: 3rem; text-align: center;">
            <p style="margin-bottom: 1.5rem; color: #555;">Unable to load quick preview.</p>
            <a href="${fallbackUrl || '#'}" class="pch-quickview__atc-btn" style="text-decoration: none; display: inline-flex; max-width: 200px;">
              View Full Details
            </a>
          </div>
        `;
      });
  }

  // --- Close Quick View ---
  function closeQuickView() {
    if (!modalEl) return;
    modalEl.classList.remove('is-active');
    modalEl.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    setTimeout(() => {
      if (!modalEl.classList.contains('is-active')) {
        contentEl.innerHTML = '';
        contentEl.style.display = 'none';
      }
    }, 350);
  }

  // --- Render Quick View Content ---
  function renderQuickView(product) {
    currentProduct = product;
    quantity = 1;

    // Pick first available variant, or first variant
    currentVariant = product.variants.find(v => v.available) || product.variants[0];
    selectedOptions = currentVariant ? [...currentVariant.options] : [];

    // Find initial image
    currentImageIndex = 0;
    if (currentVariant && currentVariant.featured_image && currentVariant.featured_image.src) {
      const idx = product.images.findIndex(img => img.includes(currentVariant.featured_image.src) || currentVariant.featured_image.src.includes(img));
      if (idx !== -1) currentImageIndex = idx;
    }

    const hasDiscount = currentVariant && currentVariant.compare_at_price > currentVariant.price;
    const discountPercent = hasDiscount
      ? Math.round(((currentVariant.compare_at_price - currentVariant.price) * 100) / currentVariant.compare_at_price)
      : 0;

    const isAvailable = currentVariant ? currentVariant.available : false;

    // Build Images HTML
    const images = product.images && product.images.length > 0 ? product.images : (product.featured_image ? [product.featured_image] : []);
    let thumbsHtml = '';
    if (images.length > 1) {
      thumbsHtml = `
        <div class="pch-quickview__thumbs" role="tablist" aria-label="Product thumbnails">
          ${images.map((img, idx) => `
            <button type="button" class="pch-quickview__thumb-btn ${idx === currentImageIndex ? 'is-active' : ''}" data-img-index="${idx}" aria-label="Thumbnail ${idx + 1}">
              <img src="${getSizedImageUrl(img, '160x')}" alt="${escapeHtml(product.title)}" loading="lazy" />
            </button>
          `).join('')}
        </div>
      `;
    }

    const arrowsHtml = images.length > 1 ? `
      <button type="button" class="pch-quickview__arrow pch-quickview__arrow--prev" data-img-prev aria-label="Previous image">
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><polyline points="15 18 9 12 15 6"></polyline></svg>
      </button>
      <button type="button" class="pch-quickview__arrow pch-quickview__arrow--next" data-img-next aria-label="Next image">
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><polyline points="9 18 15 12 9 6"></polyline></svg>
      </button>
    ` : '';

    const mainImgSrc = images[currentImageIndex] ? getSizedImageUrl(images[currentImageIndex], '1000x') : '';

    // Build Options HTML
    let optionsHtml = '';
    const hasMultipleVariants = product.variants.length > 1;
    const hasRealOptions = product.options && product.options.length > 0 && !(product.options.length === 1 && product.options[0].name === 'Title');

    if (hasMultipleVariants && hasRealOptions) {
      optionsHtml = product.options.map((option, optIdx) => {
        const optName = typeof option === 'string' ? option : option.name;
        const optValues = typeof option === 'string' ? [] : option.values;

        return `
          <div class="pch-quickview__option-group" data-option-group="${optIdx}">
            <div class="pch-quickview__option-label">
              <span>${escapeHtml(optName)}: <span class="pch-quickview__option-selected" data-opt-val="${optIdx}">${escapeHtml(selectedOptions[optIdx] || '')}</span></span>
            </div>
            <div class="pch-quickview__pills">
              ${optValues.map(val => {
                const isSelected = selectedOptions[optIdx] === val;
                // Check if this option value exists in ANY available variant
                const variantForVal = product.variants.find(v => {
                  return v.options[optIdx] === val && v.available;
                });
                const isOptAvailable = Boolean(variantForVal);

                return `
                  <button type="button"
                    class="pch-quickview__pill ${isSelected ? 'is-selected' : ''} ${isOptAvailable ? '' : 'is-disabled'}"
                    data-opt-idx="${optIdx}"
                    data-opt-value="${escapeHtml(val)}"
                    aria-label="${escapeHtml(val)}"
                  >
                    ${escapeHtml(val)}
                  </button>
                `;
              }).join('')}
            </div>
          </div>
        `;
      }).join('');
    }

    // Full Content Template
    contentEl.innerHTML = `
      <!-- LEFT: Gallery -->
      <div class="pch-quickview__gallery">
        ${thumbsHtml}
        <div class="pch-quickview__main-img-wrap">
          ${arrowsHtml}
          <img class="pch-quickview__main-img" src="${mainImgSrc}" alt="${escapeHtml(product.title)}" />
        </div>
      </div>

      <!-- RIGHT: Details -->
      <div class="pch-quickview__details">
        <div class="pch-quickview__status-wrap">
          <span class="pch-quickview__status-dot ${isAvailable ? '' : 'pch-quickview__status-dot--soldout'}"></span>
          <span class="pch-quickview__status-text ${isAvailable ? '' : 'pch-quickview__status-text--soldout'}" data-qv-status>
            ${isAvailable ? config.inStockText : config.soldOutText}
          </span>
        </div>

        <h2 class="pch-quickview__title">${escapeHtml(product.title)}</h2>

        ${product.vendor ? `<div class="pch-quickview__vendor">${escapeHtml(product.vendor)}</div>` : ''}

        <div class="pch-quickview__price-wrap">
          <span class="pch-quickview__price" data-qv-price>
            ${currentVariant ? formatMoney(currentVariant.price) : ''}
          </span>
          <span class="pch-quickview__compare-price" data-qv-compare style="${hasDiscount ? '' : 'display: none;'}">
            ${hasDiscount ? formatMoney(currentVariant.compare_at_price) : ''}
          </span>
          <span class="pch-quickview__discount-badge" data-qv-discount style="${hasDiscount ? '' : 'display: none;'}">
            -${discountPercent}% OFF
          </span>
        </div>

        <div class="pch-quickview__shipping">
          <div class="pch-quickview__shipping-title">Estimated Shipping</div>
          <div class="pch-quickview__shipping-val">${escapeHtml(config.shippingTime)}</div>
        </div>

        <div class="pch-quickview__options">
          ${optionsHtml}
        </div>

        <div class="pch-quickview__quantity-wrap">
          <div class="pch-quickview__quantity-label">Quantity</div>
          <div class="pch-quickview__quantity-ctrl">
            <button type="button" class="pch-quickview__qty-btn" data-qty-dec aria-label="Decrease quantity">&minus;</button>
            <input type="number" class="pch-quickview__qty-input" data-qty-input value="1" min="1" readonly />
            <button type="button" class="pch-quickview__qty-btn" data-qty-inc aria-label="Increase quantity">&plus;</button>
          </div>
        </div>

        <button type="button" class="pch-quickview__atc-btn" data-qv-atc ${isAvailable ? '' : 'disabled'}>
          ${isAvailable ? config.addToCartText : config.soldOutText}
        </button>

        ${config.whatsappPhone ? `
          <a href="https://wa.me/${config.whatsappPhone}?text=${encodeURIComponent('Assalam-o-Alaikum Pak Cloth House, I am interested in: ' + product.title + '\n' + (config.shopUrl || window.location.origin) + product.url)}" target="_blank" rel="noopener noreferrer" class="pch-quickview__whatsapp-btn">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M20.52 3.48A11.86 11.86 0 0 0 12.08 0C5.54 0 .22 5.32.22 11.86c0 2.09.55 4.14 1.6 5.94L.11 24l6.34-1.66a11.85 11.85 0 0 0 5.63 1.43h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.43-8.43ZM12.09 21.82h-.01c-1.76 0-3.48-.47-4.99-1.36l-.36-.21-3.76.98 1-3.66-.23-.38a9.87 9.87 0 0 1-1.51-5.33c0-5.47 4.46-9.93 9.94-9.93 2.65 0 5.14 1.03 7.01 2.91a9.87 9.87 0 0 1 2.91 7.02c0 5.47-4.46 9.93-9.94 9.93Zm5.44-7.44c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.76-1.67-2.06-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.03 1.01-1.03 2.47s1.06 2.87 1.21 3.07c.15.2 2.09 3.19 5.06 4.47.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.77-.72 2.02-1.41.25-.69.25-1.28.17-1.41-.07-.12-.27-.2-.57-.35Z"/></svg>
            <span>Ask on WhatsApp</span>
          </a>
        ` : ''}

        <div class="pch-quickview__full-details-wrap">
          <a href="${product.url}" class="pch-quickview__full-details-link">
            View Full Details &rarr;
          </a>
        </div>

        ${product.description ? `
          <div class="pch-quickview__accordion is-open">
            <button type="button" class="pch-quickview__accordion-header" data-qv-accordion>
              <span>Product Description</span>
              <span class="pch-quickview__accordion-icon">&plus;</span>
            </button>
            <div class="pch-quickview__accordion-content">
              ${product.description}
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  // --- Switch Active Image ---
  function switchImage(index) {
    if (!currentProduct || !currentProduct.images || currentProduct.images.length === 0) return;
    const total = currentProduct.images.length;
    currentImageIndex = (index + total) % total;

    const mainImg = contentEl.querySelector('.pch-quickview__main-img');
    if (mainImg) {
      mainImg.classList.add('is-switching');
      mainImg.src = getSizedImageUrl(currentProduct.images[currentImageIndex], '1000x');
      mainImg.onload = () => { mainImg.classList.remove('is-switching'); };
      setTimeout(() => { mainImg.classList.remove('is-switching'); }, 220);
    }

    // Update active thumbnail
    const thumbBtns = contentEl.querySelectorAll('.pch-quickview__thumb-btn');
    thumbBtns.forEach((btn, idx) => {
      if (idx === currentImageIndex) {
        btn.classList.add('is-active');
        btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      } else {
        btn.classList.remove('is-active');
      }
    });
  }

  // --- Select Option Value ---
  function selectOption(optIdx, value) {
    selectedOptions[optIdx] = value;

    // Update label text
    const labelEl = contentEl.querySelector(`[data-opt-val="${optIdx}"]`);
    if (labelEl) labelEl.textContent = value;

    // Update selected class in this option group
    const groupEl = contentEl.querySelector(`[data-option-group="${optIdx}"]`);
    if (groupEl) {
      groupEl.querySelectorAll('.pch-quickview__pill').forEach(btn => {
        btn.classList.toggle('is-selected', btn.dataset.optValue === value);
      });
    }

    // Find matching variant
    const matchedVariant = currentProduct.variants.find(v => {
      return v.options.every((optVal, idx) => optVal === selectedOptions[idx]);
    });

    currentVariant = matchedVariant || null;

    // Update UI for newly selected variant
    updateVariantUI();
  }

  // --- Update UI after variant changes ---
  function updateVariantUI() {
    const priceEl = contentEl.querySelector('[data-qv-price]');
    const compareEl = contentEl.querySelector('[data-qv-compare]');
    const discountEl = contentEl.querySelector('[data-qv-discount]');
    const statusDot = contentEl.querySelector('.pch-quickview__status-dot');
    const statusText = contentEl.querySelector('[data-qv-status]');
    const atcBtn = contentEl.querySelector('[data-qv-atc]');

    if (currentVariant) {
      // Update Price
      if (priceEl) priceEl.textContent = formatMoney(currentVariant.price);

      // Compare at price & Discount
      const hasDiscount = currentVariant.compare_at_price > currentVariant.price;
      if (hasDiscount) {
        const discountPercent = Math.round(((currentVariant.compare_at_price - currentVariant.price) * 100) / currentVariant.compare_at_price);
        if (compareEl) {
          compareEl.textContent = formatMoney(currentVariant.compare_at_price);
          compareEl.style.display = 'inline';
        }
        if (discountEl) {
          discountEl.textContent = `-${discountPercent}% OFF`;
          discountEl.style.display = 'inline-block';
        }
      } else {
        if (compareEl) compareEl.style.display = 'none';
        if (discountEl) discountEl.style.display = 'none';
      }

      // Stock Status
      if (currentVariant.available) {
        if (statusDot) statusDot.className = 'pch-quickview__status-dot';
        if (statusText) {
          statusText.className = 'pch-quickview__status-text';
          statusText.textContent = config.inStockText;
        }
        if (atcBtn) {
          atcBtn.removeAttribute('disabled');
          atcBtn.textContent = config.addToCartText;
        }
      } else {
        if (statusDot) statusDot.className = 'pch-quickview__status-dot pch-quickview__status-dot--soldout';
        if (statusText) {
          statusText.className = 'pch-quickview__status-text pch-quickview__status-text--soldout';
          statusText.textContent = config.soldOutText;
        }
        if (atcBtn) {
          atcBtn.setAttribute('disabled', 'disabled');
          atcBtn.textContent = config.soldOutText;
        }
      }

      // Match image if variant has featured_image
      if (currentVariant.featured_image && currentVariant.featured_image.src) {
        const imgIdx = currentProduct.images.findIndex(img => img.includes(currentVariant.featured_image.src) || currentVariant.featured_image.src.includes(img));
        if (imgIdx !== -1) switchImage(imgIdx);
      }
    } else {
      // Unavailable combination
      if (statusDot) statusDot.className = 'pch-quickview__status-dot pch-quickview__status-dot--soldout';
      if (statusText) {
        statusText.className = 'pch-quickview__status-text pch-quickview__status-text--soldout';
        statusText.textContent = 'Unavailable';
      }
      if (atcBtn) {
        atcBtn.setAttribute('disabled', 'disabled');
        atcBtn.textContent = 'Unavailable';
      }
    }

    // Refresh disabled state of sibling option pills based on availability
    if (currentProduct.options && currentProduct.options.length > 1) {
      currentProduct.options.forEach((opt, optIdx) => {
        const groupEl = contentEl.querySelector(`[data-option-group="${optIdx}"]`);
        if (!groupEl) return;
        groupEl.querySelectorAll('.pch-quickview__pill').forEach(btn => {
          const val = btn.dataset.optValue;
          const hypotheticalOptions = [...selectedOptions];
          hypotheticalOptions[optIdx] = val;
          const variantExistsAndAvailable = currentProduct.variants.some(v => {
            const matchesHypothetical = v.options.every((ov, idx) => ov === hypotheticalOptions[idx]);
            return matchesHypothetical && v.available;
          });
          btn.classList.toggle('is-disabled', !variantExistsAndAvailable);
        });
      });
    }
  }

  // --- Add to Cart Handler ---
  function addToCart() {
    if (!currentVariant || !currentVariant.available) return;

    const atcBtn = contentEl.querySelector('[data-qv-atc]');
    if (atcBtn) {
      atcBtn.classList.add('is-loading');
      atcBtn.textContent = config.addingToCartText || 'ADDING...';
      atcBtn.setAttribute('disabled', 'disabled');
    }

    const cartDrawer = document.querySelector('cart-drawer');
    const formData = new FormData();
    formData.append('id', currentVariant.id);
    formData.append('quantity', quantity);

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      const sections = cartDrawer.getSectionsToRender().map(s => s.id);
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const cartAddUrl = window.routes?.cart_add_url || '/cart/add';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then(res => {
        return res.json().then(data => {
          if (!res.ok || data.status) {
            throw new Error(data.description || data.message || 'Add to cart failed');
          }
          return data;
        });
      })
      .then(data => {
        closeQuickView();

        // Integrate with Dawn's CartDrawer
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data);
        } else {
          // Check for cart notification or redirect to cart
          const cartNotification = document.querySelector('cart-notification');
          if (cartNotification && typeof cartNotification.renderContents === 'function') {
            cartNotification.renderContents(data);
          } else if (window.routes?.cart_url) {
            window.location.href = window.routes.cart_url;
          }
        }

        // PubSub event for Dawn components
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pch-quickview', productVariantId: currentVariant.id, cartData: data });
        }
      })
      .catch(err => {
        console.error('Error adding product to cart:', err);
        if (atcBtn) {
          atcBtn.classList.remove('is-loading');
          atcBtn.removeAttribute('disabled');
          atcBtn.textContent = err.message ? err.message.slice(0, 30) : 'Error - Try Again';
          setTimeout(() => {
            atcBtn.textContent = config.addToCartText;
          }, 2500);
        }
      })
      .finally(() => {
        if (atcBtn) atcBtn.classList.remove('is-loading');
      });
  }

  // --- Global Event Delegation ---
  document.addEventListener('click', function (e) {
    // 1. Open Quick View on Eye Button Click
    const openBtn = e.target.closest('.pch-product-quickview');
    if (openBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }

      let handle = openBtn.dataset.productHandle;
      const url = openBtn.dataset.productUrl;

      if (!handle && url) {
        const match = url.match(/\/products\/([a-zA-Z0-9_-]+)/);
        if (match && match[1]) handle = match[1];
      }

      if (handle) {
        openQuickView(handle, url);
      }
      return;
    }

    // 2. Close Quick View Trigger
    if (e.target.closest('[data-pch-qv-close]')) {
      e.preventDefault();
      closeQuickView();
      return;
    }

    // If modal is not active, ignore further clicks
    if (!modalEl || !modalEl.classList.contains('is-active')) return;

    // 3. Thumbnail Click
    const thumbBtn = e.target.closest('.pch-quickview__thumb-btn');
    if (thumbBtn) {
      e.preventDefault();
      const idx = parseInt(thumbBtn.dataset.imgIndex, 10);
      if (!isNaN(idx)) switchImage(idx);
      return;
    }

    // 4. Arrow Prev / Next
    if (e.target.closest('[data-img-prev]')) {
      e.preventDefault();
      switchImage(currentImageIndex - 1);
      return;
    }
    if (e.target.closest('[data-img-next]')) {
      e.preventDefault();
      switchImage(currentImageIndex + 1);
      return;
    }

    // 5. Variant Option Pill Click
    const pill = e.target.closest('.pch-quickview__pill');
    if (pill) {
      e.preventDefault();
      const optIdx = parseInt(pill.dataset.optIdx, 10);
      const val = pill.dataset.optValue;
      if (!isNaN(optIdx) && val) {
        selectOption(optIdx, val);
      }
      return;
    }

    // 6. Quantity Controls
    if (e.target.closest('[data-qty-dec]')) {
      e.preventDefault();
      if (quantity > 1) {
        quantity--;
        const input = contentEl.querySelector('[data-qty-input]');
        if (input) input.value = quantity;
      }
      return;
    }
    if (e.target.closest('[data-qty-inc]')) {
      e.preventDefault();
      quantity++;
      const input = contentEl.querySelector('[data-qty-input]');
      if (input) input.value = quantity;
      return;
    }

    // 7. Add to Cart Click
    if (e.target.closest('[data-qv-atc]')) {
      e.preventDefault();
      addToCart();
      return;
    }

    // 8. Description Accordion Toggle
    const accordionHeader = e.target.closest('[data-qv-accordion]');
    if (accordionHeader) {
      e.preventDefault();
      const accordion = accordionHeader.closest('.pch-quickview__accordion');
      if (accordion) accordion.classList.toggle('is-open');
      return;
    }
  }, true);

  // --- Keyboard Dismiss (ESC) ---
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modalEl && modalEl.classList.contains('is-active')) {
      closeQuickView();
    }
  });

  // Expose API for developer console / external scripts
  window.PchQuickView = {
    open: openQuickView,
    close: closeQuickView
  };
})();
